// Annotation clipboard: copy writes a tagged JSON envelope to the OS
// clipboard (so paste works across tabs and sessions, and stale OS
// content never shadows a fresh annotation copy), paste validates and
// re-instantiates with fresh ids on the target page.
import { annotBounds, shiftAnnotWithPage } from "./annot-geometry.ts";
import { isMarkup, type Annotation } from "./protocol.ts";
import { nextAnnotId } from "./state.ts";

/** Envelope key — presence marks clipboard text as ours. */
const CLIPBOARD_KEY = "xtract-pdf-editor/annotations@1";

const KNOWN_KINDS = new Set<Annotation["kind"]>([
  "highlight", "underline", "strikeout", "squiggly",
  "text", "note", "ink", "shape", "stamp", "redact", "link",
]);

export function serializeAnnots(annots: readonly Annotation[]): string {
  return JSON.stringify({ [CLIPBOARD_KEY]: annots });
}

/** Parse clipboard text; null unless it's our envelope with a sane
 *  annotation list. */
export function parseAnnotClipboard(text: string): Annotation[] | null {
  if (!text.includes(CLIPBOARD_KEY)) return null;
  try {
    const obj = JSON.parse(text) as Record<string, unknown>;
    const list = obj[CLIPBOARD_KEY];
    if (!Array.isArray(list) || list.length === 0) return null;
    const ok = list.every(
      (a) =>
        a &&
        typeof a === "object" &&
        KNOWN_KINDS.has((a as Annotation).kind) &&
        typeof (a as Annotation).pageIndex === "number"
    );
    return ok ? (list as Annotation[]) : null;
  } catch {
    return null;
  }
}

/** Shift an annotation so its bounds sit inside the page (best effort —
 *  oversized content is pinned to the top-left). */
export function clampAnnotToPage(a: Annotation, pageWidth: number, pageHeight: number): Annotation {
  const b = annotBounds(a);
  let dx = 0;
  let dy = 0;
  if (b[2] > pageWidth) dx = pageWidth - b[2];
  if (b[3] > pageHeight) dy = pageHeight - b[3];
  if (b[0] + dx < 0) dx = -b[0];
  if (b[1] + dy < 0) dy = -b[1];
  return dx === 0 && dy === 0 ? a : shiftAnnotWithPage(a, dx, dy);
}

/** Materialise clipboard annotations onto `pageIndex`: fresh ids, a
 *  cascade offset so repeated pastes don't stack exactly, clamped to
 *  the page. Markup keeps its quads (it pastes where the source text
 *  was — visually sensible only on similar pages, but never invalid). */
export function cloneForPaste(
  list: readonly Annotation[],
  pageIndex: number,
  offset: number,
  pageWidth: number,
  pageHeight: number
): Annotation[] {
  return list.map((src) => {
    const a: Annotation = { ...structuredClone(src), id: nextAnnotId(), pageIndex };
    const shifted = offset !== 0 ? shiftAnnotWithPage(a, offset, offset) : a;
    return clampAnnotToPage(shifted, pageWidth, pageHeight);
  });
}

/** True when the annotation kind participates in copy/paste. (All do —
 *  kept explicit so future kinds make a deliberate choice.) */
export function copyable(a: Annotation): boolean {
  return KNOWN_KINDS.has(a.kind) || isMarkup(a);
}
